import { motion } from 'framer-motion';
import AnimatedPage from '../components/AnimatedPage';
import { Heart, Target, Zap, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion',
      description: 'We write with heart and authenticity, sharing stories that matter.',
    },
    {
      icon: Target,
      title: 'Purpose',
      description: 'Every article aims to inspire, educate, or spark meaningful conversations.',
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Embracing modern storytelling techniques and fresh perspectives.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Building a space where readers and writers connect and grow together.',
    },
  ];

  return (
    <AnimatedPage>
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="heading-1 mb-6">About RaiBlog</h1>
              <div className="w-20 h-1 bg-black mx-auto mb-8" />
              <p className="body-text text-gray-600 leading-relaxed">
                Welcome to RaiBlog, a modern platform where ideas come to life through
                the power of words. We believe in the transformative nature of
                storytelling and its ability to connect, inspire, and educate.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div>
                <h2 className="heading-2 mb-6">Our Mission</h2>
                <p className="body-text text-gray-700 mb-4">
                  At RaiBlog, we're on a mission to create a space where authentic
                  voices can be heard and meaningful content can flourish. We curate
                  and create articles that span various topics, from technology and
                  lifestyle to philosophy and creative arts.
                </p>
                <p className="body-text text-gray-700">
                  Our commitment is to deliver quality content that not only informs
                  but also encourages our readers to think critically and engage
                  deeply with the world around them.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden">
                 <img 
                  src="https://i.postimg.cc/qvTb3f7w/Picsart-25-10-17-13-44-26-573.jpg" 
                  alt="Our Mission" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="heading-2 mb-4">Our Core Values</h2>
              <p className="body-text text-gray-600 max-w-2xl mx-auto">
                These principles guide everything we do at RaiBlog
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  className="text-center p-6 bg-white border border-gray-200 rounded-xl hover:shadow-xl transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-4">
                    <value.icon size={28} />
                  </div>
                  <h3 className="heading-4 mb-3">{value.title}</h3>
                  <p className="text-gray-600 font-body text-sm">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Author Section */}
        <section className="py-20 px-4 bg-black text-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://i.postimg.cc/jq3ZZwSS/raiblogs-rai.jpg" 
                alt="Rai Mohan" 
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h2 className="heading-3 mb-4">Rai Mohan</h2>
              <p className="text-gray-400 font-body mb-6">Founder & Lead Writer</p>
              <p className="body-text text-gray-300 max-w-2xl mx-auto">
                A passionate writer, developer, and creative thinker dedicated to
                exploring the intersection of technology, philosophy, and human
                experience. Through RaiBlog, I aim to share insights that inspire
                growth and spark curiosity.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-2 mb-6">Join Our Journey</h2>
              <p className="body-text text-gray-600 mb-8">
                Be part of our growing community of readers and thinkers. Subscribe
                to our newsletter to stay updated with the latest articles.
              </p>
              <motion.a
                href="/"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block px-8 py-4 bg-black text-white rounded-lg font-body font-semibold hover:bg-gray-800 transition-colors"
              >
                Explore Articles
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </AnimatedPage>
  );
};

export default About;

